/* eslint-disable react/require-default-props */
import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart3,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  History,
  Home,
  Loader2,
  Shield,
  Star,
  TrendingUp,
  TreePine,
  Users,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useHistory, useLocation } from 'react-router-dom';

import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import userRoles from '../constants/userRoles';
import UserContext from '../hooks/context/UserContext';
import { cn } from '../lib/utils';
import axios from '../services/http.service';
import Header from './Header';

const DEPARTMENT_MENU_LIST = [
  { title: 'Dashboard', route: 'dashboard', icon: BarChart3 },
  { title: 'Users', route: 'users', icon: Users },
  { title: 'Grow', route: 'grow', icon: TreePine },
  { title: 'Levels', route: 'levels', icon: TrendingUp },
  { title: 'Reviews', route: 'reviews', icon: Star },
  { title: 'Review History', route: 'review-history', icon: History },
];

const SIDEBAR_COLLAPSED_KEY = 'grow-head-sidebar-collapsed';
const EXPANDED_DEPTS_KEY = 'grow-head-expanded-depts';

function NavItem({ item, isCollapsed, isActive, onClick }) {
  const Icon = item.icon;

  const content = (
    <button
      type="button"
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-3 h-9 text-sm font-medium transition-colors',
        'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
        isCollapsed && 'justify-center px-2',
        isActive && 'bg-sidebar-accent text-sidebar-foreground',
      )}
      onClick={onClick}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!isCollapsed && <span className="truncate">{item.title}</span>}
    </button>
  );

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {item.title}
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}

NavItem.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    route: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
  }).isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

function DepartmentSection({
  department,
  isCollapsed,
  expandedDepts,
  onToggleExpand,
  currentPath,
}) {
  const history = useHistory();
  const isExpanded = expandedDepts.includes(department._id);

  const handleNavigate = (route) => {
    history.push(`/head/departments/${department.name}/${route}`);
  };

  const isDeptActive = currentPath.includes(`/head/departments/${department.name}`);

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full items-center justify-center rounded-md px-2 h-9 transition-colors',
              'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
              isDeptActive && 'bg-primary/10 text-primary',
            )}
            onClick={() => handleNavigate('dashboard')}
          >
            <Building2 className="h-4 w-4 shrink-0" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {department.title}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-3 h-9 text-sm font-medium transition-colors',
          'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
          isDeptActive && 'text-sidebar-foreground',
        )}
        onClick={() => onToggleExpand(department._id)}
      >
        <Building2 className="h-4 w-4 shrink-0" />
        <span className="truncate flex-1 text-left">{department.title}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', isExpanded && 'rotate-180')}
        />
      </button>

      {isExpanded && (
        <div className="ml-4 pl-3 border-l border-sidebar-border space-y-1">
          {DEPARTMENT_MENU_LIST.map((item) => {
            const isActive = currentPath.includes(`/${department.name}/${item.route}`);
            return (
              <NavItem
                key={item.route}
                item={item}
                isCollapsed={false}
                isActive={isActive}
                onClick={() => handleNavigate(item.route)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

DepartmentSection.propTypes = {
  department: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  expandedDepts: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  currentPath: PropTypes.string.isRequired,
};

function HeadSidebar({ isCollapsed, onToggle, departments, isLoading, isAdmin }) {
  const history = useHistory();
  const location = useLocation();
  const [expandedDepts, setExpandedDepts] = useState(() => {
    try {
      const saved = localStorage.getItem(EXPANDED_DEPTS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Auto-expand current department
  useEffect(() => {
    if (departments.length > 0) {
      const currentDept = departments.find((d) =>
        location.pathname.includes(`/head/departments/${d.name}`),
      );
      if (currentDept && !expandedDepts.includes(currentDept._id)) {
        setExpandedDepts((prev) => [...prev, currentDept._id]);
      }
    }
  }, [departments, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem(EXPANDED_DEPTS_KEY, JSON.stringify(expandedDepts));
  }, [expandedDepts]);

  const handleToggleExpand = (deptId) => {
    setExpandedDepts((prev) =>
      prev.includes(deptId) ? prev.filter((id) => id !== deptId) : [...prev, deptId],
    );
  };

  const handleNavigation = (path) => {
    history.push(path);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64',
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo Section */}
          <div
            className={cn(
              'flex h-16 items-center border-b border-sidebar-border px-4',
              isCollapsed ? 'justify-center' : 'justify-between',
            )}
          >
            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                  <span className="text-lg font-bold text-primary-foreground">G</span>
                </div>
                <div>
                  <span className="text-lg font-semibold text-sidebar-foreground">Grow</span>
                  <span className="ml-1 text-xs text-muted-foreground">Head</span>
                </div>
              </div>
            )}
            {isCollapsed && (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <span className="text-lg font-bold text-primary-foreground">G</span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            {!isCollapsed && (
              <p className="px-3 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Departments
              </p>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <nav className="flex flex-col gap-1">
                {departments.map((department) => (
                  <DepartmentSection
                    key={department._id}
                    department={department}
                    isCollapsed={isCollapsed}
                    expandedDepts={expandedDepts}
                    onToggleExpand={handleToggleExpand}
                    currentPath={location.pathname}
                  />
                ))}
              </nav>
            )}

            {/* Panel Links Section */}
            <div className={cn('my-4 border-t border-sidebar-border', isCollapsed && 'mx-2')} />
            {!isCollapsed && (
              <p className="px-3 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Switch Panel
              </p>
            )}
            <nav className="flex flex-col gap-1">
              <NavItem
                item={{ title: 'Users Panel', route: '/home', icon: Home }}
                isCollapsed={isCollapsed}
                isActive={false}
                onClick={() => handleNavigation('/home')}
              />
              {isAdmin && (
                <NavItem
                  item={{ title: 'Admin Panel', route: '/admin/departments', icon: Shield }}
                  isCollapsed={isCollapsed}
                  isActive={false}
                  onClick={() => handleNavigation('/admin/departments')}
                />
              )}
            </nav>
          </ScrollArea>

          {/* Collapse Toggle */}
          <div
            className={cn('border-t border-sidebar-border p-3', !isCollapsed && 'flex justify-end')}
          >
            <button
              type="button"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
              )}
              onClick={onToggle}
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
}

HeadSidebar.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  departments: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

function HeadLayout({ children, title }) {
  const { user } = useContext(UserContext);
  const isAdmin = user?.roles?.includes(userRoles.ADMIN);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved ? JSON.parse(saved) : false;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch departments
  useEffect(() => {
    async function fetchDepartments() {
      try {
        const data = await axios({
          method: 'GET',
          url: 'head/departments/',
        });
        setDepartments(data);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchDepartments();
  }, []);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  return (
    <>
      <Helmet>
        <title>{title} | Grow</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <HeadSidebar
            isCollapsed={isSidebarCollapsed}
            onToggle={toggleSidebar}
            departments={departments}
            isLoading={isLoading}
            isAdmin={isAdmin}
          />
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              className="fixed inset-0 z-30 bg-black/50 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="fixed left-0 top-0 z-40 lg:hidden">
              <HeadSidebar
                isCollapsed={false}
                onToggle={() => setIsMobileMenuOpen(false)}
                departments={departments}
                isLoading={isLoading}
                isAdmin={isAdmin}
              />
            </div>
          </>
        )}

        {/* Main Content */}
        <div
          className={cn(
            'transition-all duration-300 h-screen flex flex-col',
            isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64',
          )}
        >
          <Header title={title} onMenuClick={toggleMobileMenu} showMenuButton />
          <main className="p-6 flex-1 overflow-hidden flex flex-col">{children}</main>
        </div>
      </div>
    </>
  );
}

HeadLayout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
};

export default HeadLayout;
